import { dehydrate, QueryClient } from '@tanstack/react-query'
import clsx from '@vangst/lib/clsx'
import omitEmptyOrNil from '@vangst/lib/omitEmptyOrNil'
import stateSlugToAbbreviation from '@vangst/lib/stateSlugToAbbreviation'
import titleize from '@vangst/lib/titleize'
import { prefetchStateDetail } from '@vangst/services/graphcms/marketing/useStateDetail'
import { fetchPage } from '@vangst/services/oogst/page'
import {
  ElasticSearchEndpoints,
  ElasticSearchVars,
} from '@vangst/services/oogst/search/useElasticSearch'
import {
  CountriesEnum,
  RobotsEnum,
  StatesEnum,
} from '@vangst/services/oogst/types'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import Image from 'next/image'
import { MdDescription } from 'react-icons/md'
import Busy from '../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../components/navigation/Breadcrumbs'
import ClickyLink from '../../../../components/navigation/ClickyLink'
import GroupHeader from '../../../../components/views/GroupHeader'
import Markdown from '../../../../components/views/Markdown'
import PageHeader from '../../../../components/views/PageHeader'
import Meta from '../../../../domains/application/Meta'
import ConnectSearchSidebar from '../../../../domains/marketing/ConnectSearchSidebar'
import StateRequirementsHeaderSelect from '../../../../domains/marketing/StateRequirementsHeaderSelect'
import SearchRenderer from '../../../../domains/search/SearchRenderer'

type StaticProps = GetStaticPropsContext<{ slug: string }>

type SearchType = {
  title: string
  endpoint: ElasticSearchEndpoints
  variables: ElasticSearchVars
}

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const metaSlug = params?.slug ? params.slug : undefined
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[slug]', metaSlug || '')

  const title = metaSlug != null ? `In ${titleize(metaSlug)}` : 'By State'
  const { getPage } = await fetchPage(queryClient, { rawUrl })

  const page = {
    ...getPage,
    title: getPage?.title ?? `How To Work At A Dispensary ${title}`,
  }

  const abrev =
    params?.slug != null
      ? (stateSlugToAbbreviation(params.slug) as keyof typeof StatesEnum)
      : undefined

  const search: SearchType = {
    title: `Latest Cannabis Jobs In ${titleize(metaSlug || '')}`,
    endpoint: 'searchJobPostings',
    variables: {
      first: 6,
      matchLocation: {
        state: StatesEnum[abrev as keyof typeof StatesEnum] || null,
        country: CountriesEnum['UnitedStates'],
      },
    },
  }

  let state
  let delivery
  let stats

  if (metaSlug) {
    state = await prefetchStateDetail(queryClient, { metaSlug })
  }

  if (state) {
    delivery =
      state?.delivery?.toLowerCase() === 'all'
        ? 'Medical & Adult Use'
        : state?.delivery.split('_').join(' ')

    stats = [
      { name: 'Medical', data: state?.medical },
      { name: 'Adult Use', data: state?.adultUse },
      { name: 'Hemp', data: 'Yes' },
      { name: 'CBD', data: state?.cbd },
      { name: 'Delivery', data: delivery },
      {
        name: 'Fees',
        data: state?.fees != null ? `$${state.fees}` : 'N/A',
      },
      {
        name: 'Application',
        data:
          state?.linkToApplication != null ? state?.linkToApplication : null,
      },
      { name: 'Age', data: state?.age },
      {
        name: 'Hiring Timeline',
        data: state?.hiringTimeline || 'Not Specified',
      },
    ]
  }

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: Routes.REPORTS, text: 'Reports' },
    { href: Routes.REPORTS_STATE_REQUIREMENTS, text: 'State Requirements' },
    { href: null, text: titleize(metaSlug ?? '') },
  ]

  return {
    props: {
      dehydratedState: omitEmptyOrNil(dehydrate(queryClient)),
      crumbs,
      metaSlug,
      page,
      search: search || null,
      state,
      stats,
    },
    notFound: state == null,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

export default function StateRequirementsDetailPage(props: PropsType) {
  const { crumbs, page, metaSlug, search, state, stats } = props

  if (!metaSlug) {
    return (
      <div className="contain-xyc">
        <Busy />
      </div>
    )
  }

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page?.metaInfo?.canonicalUrl}
        crumbs={crumbs}
        description={page?.metaInfo?.description}
        image={page?.pagePhotos?.[0]?.url}
        publishDate={page?.postedAt}
        robots={page?.metaInfo?.robots ?? RobotsEnum.IndexFollow}
        title={page?.metaInfo?.title ?? page?.title}
      />
      <Breadcrumbs crumbs={crumbs} />

      <article className="mood-white">
        <PageHeader
          icon="pin drop"
          accent="text-blue"
          title={page?.title}
          subtitle={page?.subtitle}
          description={page?.description}
          imgAlt={page?.pagePhotos?.[0]?.alt}
          imgSrc={page?.pagePhotos?.[0]?.url}
        />
      </article>
      <StateRequirementsHeaderSelect />
      {page?.markdownContent && (
        <section className="contain-xyc mood-white">
          <div className="text-sm">
            <Markdown renderType="basic">{page.markdownContent}</Markdown>
          </div>
        </section>
      )}

      <div className="contain-xyc mood-white grid gap-8 py-16">
        <div className="mood-white grid content-start gap-6 sm:grid-cols-[3fr_1fr] sm:gap-12">
          {state != null && (
            <div className="grid content-start gap-4">
              <div className="flow-y-sm sm:flow-x-sm">
                <div className="flex items-center">
                  <div className="mood-grey relative flex size-24 items-center justify-center self-start rounded-full p-4 sm:size-48">
                    {state.image && (
                      <Image
                        alt={`Shape of ${state.stateName ?? 'state'}`}
                        className="size-auto object-contain"
                        src={state.image}
                        width={120}
                        height={120}
                      />
                    )}
                  </div>
                  <h2 className="ml-4 block heading-md sm:hidden">
                    Cannabis Licensing in {state.stateName}
                  </h2>
                </div>
                <div className="flow-y-sm">
                  <h2 className="hidden heading-md sm:block">
                    {state.stateName}
                  </h2>
                  {stats != null && (
                    <div className="grid-three-up">
                      {stats.map((stat) => (
                        <dl
                          key={`stat-${stat.name}`}
                          className="flex items-baseline gap-2"
                        >
                          <dt className="text-lg font-600">{stat.name}:</dt>
                          <dd className="capitalize">
                            {stat?.name === 'Application' ? (
                              <ClickyLink
                                disabled={stat?.data == null}
                                href={stat?.data ?? ''}
                                className={clsx(
                                  stat?.data == null
                                    ? 'text-black'
                                    : 'link-orange',
                                )}
                              >
                                {stat?.data != null ? 'Link' : 'N/A'}
                              </ClickyLink>
                            ) : (
                              <span>{stat.data}</span>
                            )}
                          </dd>
                        </dl>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <hr className="my-4 sm:my-12" />
              <div className="flow-y-sm">
                <dl>
                  <dt className="heading-sm">Cannabis Job Market Overview:</dt>
                  <dd>
                    <Markdown renderType="blog">
                      {state.licenseRequired ?? 'N/A'}
                    </Markdown>
                  </dd>
                </dl>
                <dl>
                  <dt className="heading-sm">
                    Cannabis Job Requirements & Application Process:
                  </dt>
                  <dd>
                    <Markdown renderType="blog">
                      {state.applicationRequirements ?? 'N/A'}
                    </Markdown>
                  </dd>
                </dl>
                <dl>
                  <dt className="heading-sm">
                    Cannabis Job Background Check Requirements:
                  </dt>
                  <dd>
                    <Markdown renderType="blog">
                      {state.backgroundCheckRequirements ?? 'N/A'}
                    </Markdown>
                  </dd>
                </dl>
                <dl>
                  <dt className="heading-sm">Additional Links:</dt>
                  <dd>
                    <Markdown renderType="blog">
                      {state.additionalLinks ?? 'N/A'}
                    </Markdown>
                  </dd>
                </dl>
                <dl>
                  <dt className="heading-sm">
                    Additional Job & Salary Information:
                  </dt>
                  <dd className="py-4">
                    <Markdown renderType="blog">
                      {state.jobsSalaryInfo ?? 'N/A'}
                    </Markdown>
                  </dd>
                </dl>
              </div>

              <hr className="my-4" />
              <div className="my-4">
                <GroupHeader
                  icon={MdDescription}
                  className="mb-8"
                  title="Latest Jobs"
                  to={`${Routes.JOBS}?matchLocation=state=${state.stateAbbreviation}`}
                  toLabel={`Search All ${state.stateAbbreviation} Cannabis Jobs`}
                >
                  <span className="mt-4 lg:ml-8">
                    Search a vast pool of jobs catering to everyone from
                    enthusiasts to experts.
                  </span>
                </GroupHeader>

                <SearchRenderer
                  as="card"
                  at="infinite-preview"
                  classNameList="grid gap-16 md:grid-cols-3"
                  endpoint={search.endpoint}
                  variables={search.variables}
                />
              </div>
            </div>
          )}

          <ConnectSearchSidebar />
        </div>
      </div>
    </>
  )
}
